import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useQuery } from "@tanstack/react-query";
import {
  Bar,
  BarChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";

import { pricesApi } from "@/api/prices";
import { fmt, hourFromTs, num, todayIso } from "@/lib/format";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";

export function RdnPage() {
  const { t } = useTranslation();
  const [date, setDate] = useState(todayIso());

  const rdn = useQuery({
    queryKey: ["rdn-prices", date],
    queryFn: () => pricesApi.rdn(date),
  });
  const imsp = useQuery({
    queryKey: ["imbalance-prices", date],
    queryFn: () => pricesApi.imbalance(date),
  });

  const chartData = useMemo(() => {
    const rows = rdn.data ?? [];
    const imspByHour = new Map<number, number>();
    (imsp.data ?? []).forEach((p) => {
      const h = hourFromTs(p.ts);
      const v = num(p.price_uah_per_mwh);
      if (v !== null) imspByHour.set(h, v);
    });
    return rows.map((p) => {
      const h = hourFromTs(p.ts);
      return {
        hour: String(h).padStart(2, "0"),
        rdn: num(p.price_uah_per_mwh) ?? 0,
        imsp: imspByHour.get(h) ?? null,
      };
    });
  }, [rdn.data, imsp.data]);

  const stats = useMemo(() => {
    const vals = chartData.map((r) => r.rdn).filter((v) => v > 0);
    if (vals.length === 0) return null;
    const min = Math.min(...vals);
    const max = Math.max(...vals);
    const avg = vals.reduce((a, b) => a + b, 0) / vals.length;
    return { min, max, avg };
  }, [chartData]);

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle>{t("rdn.title")}</CardTitle>
              <CardDescription>{t("rdn.subtitle")}</CardDescription>
            </div>
            <a
              href="https://www.oree.com.ua/index.php/pricectr"
              target="_blank"
              rel="noreferrer"
              className="text-xs text-blue-600 hover:underline"
            >
              oree.com.ua →
            </a>
          </div>
        </CardHeader>
        <CardContent>
          <div className="space-y-1 w-fit">
            <Label>{t("plan.date")}</Label>
            <Input
              type="date"
              value={date}
              onChange={(e) => setDate(e.target.value)}
              className="w-[180px]"
            />
          </div>
        </CardContent>
      </Card>

      {stats && (
        <div className="grid grid-cols-3 gap-3">
          <Stat label={t("rdn.min")} value={fmt(stats.min)} />
          <Stat label={t("rdn.avg")} value={fmt(stats.avg)} />
          <Stat label={t("rdn.max")} value={fmt(stats.max)} />
        </div>
      )}

      <Card>
        <CardHeader>
          <CardTitle className="text-base">{t("rdn.chart_title")}</CardTitle>
        </CardHeader>
        <CardContent>
          {rdn.isLoading ? (
            <div className="text-sm text-slate-500">{t("common.loading")}</div>
          ) : chartData.length === 0 ? (
            <div className="text-sm text-slate-500">{t("rdn.not_published")}</div>
          ) : (
            <div className="h-72">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={chartData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="hour" />
                  <YAxis tickFormatter={(v) => fmt(v as number)} />
                  <Tooltip formatter={(v) => fmt(v as number)} />
                  <Bar dataKey="rdn" name={t("rdn.price")} fill="#2563eb" />
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}
        </CardContent>
      </Card>

      {chartData.length > 0 && (
        <Card>
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-50">
                  <tr className="text-left text-slate-500">
                    <th className="p-2">{t("common.hour")}</th>
                    <th className="p-2 text-right">{t("rdn.price")}</th>
                    <th className="p-2 text-right">{t("rdn.imsp")}</th>
                  </tr>
                </thead>
                <tbody>
                  {chartData.map((r) => (
                    <tr key={r.hour} className="border-t border-slate-100">
                      <td className="p-2 font-mono">{r.hour}:00</td>
                      <td className="p-2 text-right font-mono">{fmt(r.rdn)}</td>
                      <td className="p-2 text-right font-mono text-slate-500">
                        {r.imsp === null ? "—" : fmt(r.imsp)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <Card>
      <CardContent className="p-4">
        <div className="text-xs text-slate-500">{label}</div>
        <div className="text-xl font-mono font-semibold">{value}</div>
      </CardContent>
    </Card>
  );
}
